import axios, { AxiosError } from 'axios';
import qs from 'qs';
import { ENV } from '../lib/env';
import {
  ApiErrorDtoSchema,
  coerceApiErrorResponse,
  normalizeApiErrorResponse,
} from './lib';
import { API } from './modules';

export const BACKEND_API_VERSION = 'v1';

export const api = axios.create({
  baseURL: `${ENV.backendApiUrl}/${BACKEND_API_VERSION}`,
  withCredentials: true,
  paramsSerializer: {
    serialize: (params) => {
      return qs.stringify(params, {
        arrayFormat: 'repeat',
        skipNulls: true,
      });
    },
  },
});

let authTokenGetter: () => string | null = () => null;

/**
 * The current access token, for the few requests that cannot go through `api`
 * — a streamed response has to be read with `fetch`, which axios does not
 * expose in the browser.
 */
export const getAuthToken = () => authTokenGetter();

export function attachAuthInterceptor(
  getAuthToken: () => string | null,
  setAccessToken: (accessToken: string) => void,
  logout: () => void
) {
  authTokenGetter = getAuthToken;

  api.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    async (error) => {
      return Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      /* Access Token expiration */
      const originalRequest = error.config;
      if (
        error.response?.status === 401 &&
        error.response?.data?.message === 'session_expired' &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        try {
          const { accessToken } = await API.auth.refreshToken();

          setAccessToken(accessToken);

          return api(originalRequest);
        } catch (error) {
          logout();
          window.location.href = '/';
          return Promise.reject(error);
        }
      }

      /* Validation + Normalization */
      if (!axios.isAxiosError(error)) {
        return Promise.reject(error);
      }

      // A body that fails the schema is still usable — the status is on the
      // response, and the server's own message beats a generic string. Only a
      // request with no response at all is truly unreportable.
      const validation = ApiErrorDtoSchema.safeParse(error.response?.data);
      const apiError = validation.success
        ? validation.data
        : coerceApiErrorResponse(error);

      if (!apiError) {
        return Promise.reject(
          'An unexpected error occurred while parsing the API response.'
        );
      }

      // Spread the raw body first so endpoint-specific detail survives
      // normalization — the staff import, for one, returns a per-row `errors`
      // array the modal renders. The normalized fields still win.
      const rawData =
        typeof error.response?.data === 'object' && error.response.data !== null
          ? error.response.data
          : {};

      const normalizedErrorResponse = {
        ...error.response!,
        data: { ...rawData, ...normalizeApiErrorResponse(apiError) },
      };

      return Promise.reject(
        new AxiosError(
          error.message,
          error.code,
          error.config,
          error.request,
          normalizedErrorResponse
        )
      );
    }
  );
}
